import type { MsgKey } from "./i18n";

/**
 * idol 相册（关卡相册）/ VIP 照片 / VIP 视频的前端模型（服务端 src/admin/idol-album.ts）。
 *
 * 三张表都照拼图那套走（lib/puzzle.ts），逐条照抄、别另起炉灶：
 * - **ID 由服务端分配**（`ph_` / `vp_` / `vv_`）。`null` = 服务端还不知道这一行（本地新建、尚未提交），
 *   不用 `tmp_N` 那种前缀约定——null 不可能被误当成合法 ID 发出去；
 * - `localKey` 是纯本地标识，新建时是 UUID，随建行请求作为 `uid` 落库；读回来的行 localKey = uid，
 *   所有人屏幕上同一行的本地键一样，回包丢了也能按它认领（lib/autosave.ts reconcileLostCreates）；
 * - 页面只改 App 的 state，顶栏保存逐行 POST / PUT / DELETE、接力 revision。
 *
 * 规则与等级是否配套（`ruleLevelFault`）**抄的是**服务端 config-validate.ts 的 `albumRuleFault`，
 * 那边是写接口与发布闸共用的唯一一处判定；这里只为当场标红，真正拦的是服务端。
 */

/** 关卡相册的解锁规则。与服务端 IDOL_PHOTO_UNLOCK_RULES 一致 */
export const PHOTO_UNLOCK_RULES = ["stage_redeemed", "stage_assembled", "intimacy_lv", "vip", "free"] as const;
/** VIP 照片 / 视频的锁定规则。与服务端 IDOL_VIP_LOCK_RULES 一致 */
export const VIP_LOCK_RULES = ["intimacy_lv", "vip", "free"] as const;
/** 只有这条规则看亲密度等级（且必须 ≥ 1）；其它规则等级必须是 0 */
export const RULE_INTIMACY = "intimacy_lv";
/** 相册 slot 下界：第 1 张是拼图完成照，由服务端用拼图的「完成图」合成，不配在这张表里 */
export const PHOTO_SLOT_MIN = 2;
/** 与服务端 INT32_MAX 一致：slot / sortOrder / 等级那几列存得下的最大整数 */
export const ALBUM_INT_MAX = 2147483647;

// ─────────────────────────────────────────────────────────────────────────────
// 形状
// ─────────────────────────────────────────────────────────────────────────────

/** 关卡相册一行（cfg_idol_photo）。挂在某张拼图（关卡）下，第 2 张起 */
export interface IdolPhoto {
  /** 服务端分配的 `ph_NNNN`；null = 本地新建、尚未提交 */
  photoId: string | null;
  /** 纯本地标识；新建时随 POST 作为 uid 落库。永不当成服务端 ID 用 */
  localKey: string;
  puzzleId: string;
  /** 这一关里的第几张，≥ 2。草稿里允许暂时重复（对调两张时中间态必然重复），发布闸查启用行唯一 */
  slot: number;
  /** 缩略图：未解锁的玩家也看得到 */
  thumbImg: string;
  img: string;
  unlockRule: string;
  unlockIntimacyLv: number;
  enabled: boolean;
}

/** VIP 照片一行（cfg_idol_vip_photo） */
export interface IdolVipPhoto {
  vipPhotoId: string | null;
  localKey: string;
  idolId: string;
  thumbImg: string;
  img: string;
  lockRule: string;
  unlockIntimacyLv: number;
  /** 升序；同值按 ID */
  sortOrder: number;
  /** UTC 毫秒；0 = 立即 */
  publishAtMs: number;
  enabled: boolean;
}

/** VIP 视频一行（cfg_idol_vip_video）。时长与 videoUrl 随视频上传回来，运营不手填 */
export interface IdolVipVideo {
  vipVideoId: string | null;
  localKey: string;
  idolId: string;
  coverImg: string;
  /** 秒；null = 视频还没传。**只读**：随上传回来，运营不能改 */
  durationSec: number | null;
  /** 视频文件的完整 CDN 地址（上传接口回来的 url）；null = 还没传。只读 */
  videoUrl: string | null;
  lockRule: string;
  unlockIntimacyLv: number;
  sortOrder: number;
  publishAtMs: number;
  enabled: boolean;
}

/** 服务端行的形状（GET 回来的 rows） */
export interface ServerIdolPhoto extends Omit<IdolPhoto, "photoId" | "localKey"> { photoId: string; uid?: string }
export interface ServerIdolVipPhoto extends Omit<IdolVipPhoto, "vipPhotoId" | "localKey"> { vipPhotoId: string; uid?: string }
export interface ServerIdolVipVideo extends Omit<IdolVipVideo, "vipVideoId" | "localKey"> { vipVideoId: string; uid?: string }

/** 请求体的内容部分（POST 新建 / PUT 更新共用）。不含 ID：新建时还没有，更新时由调用方单独带 */
export type IdolPhotoBody = Omit<ServerIdolPhoto, "photoId">;
export type IdolVipPhotoBody = Omit<ServerIdolVipPhoto, "vipPhotoId">;
export type IdolVipVideoBody = Omit<ServerIdolVipVideo, "vipVideoId">;

// ─────────────────────────────────────────────────────────────────────────────
// 服务端 ↔ 前端
// ─────────────────────────────────────────────────────────────────────────────

const num = (v: unknown, dflt = 0) => (v == null || v === "" || !Number.isFinite(Number(v)) ? dflt : Number(v));

/**
 * 规则原样保留，不洗成默认值——库里约定外的值要在界面上标红（同拼图的 photoType），
 * 洗掉的话界面看着正常、发布却按原值被拒，运营无从下手。
 */
export const toIdolPhoto = (r: ServerIdolPhoto): IdolPhoto => ({
  photoId: r.photoId,
  localKey: r.uid || r.photoId,
  puzzleId: r.puzzleId ?? "",
  slot: num(r.slot),
  thumbImg: r.thumbImg ?? "",
  img: r.img ?? "",
  unlockRule: r.unlockRule ?? "",
  unlockIntimacyLv: num(r.unlockIntimacyLv),
  enabled: !!r.enabled,
});

export const toIdolVipPhoto = (r: ServerIdolVipPhoto): IdolVipPhoto => ({
  vipPhotoId: r.vipPhotoId,
  localKey: r.uid || r.vipPhotoId,
  idolId: r.idolId ?? "",
  thumbImg: r.thumbImg ?? "",
  img: r.img ?? "",
  lockRule: r.lockRule ?? "",
  unlockIntimacyLv: num(r.unlockIntimacyLv),
  sortOrder: num(r.sortOrder),
  publishAtMs: num(r.publishAtMs),
  enabled: !!r.enabled,
});

export const toIdolVipVideo = (r: ServerIdolVipVideo): IdolVipVideo => ({
  vipVideoId: r.vipVideoId,
  localKey: r.uid || r.vipVideoId,
  idolId: r.idolId ?? "",
  coverImg: r.coverImg ?? "",
  durationSec: r.durationSec == null ? null : num(r.durationSec),
  videoUrl: r.videoUrl ? String(r.videoUrl) : null,
  lockRule: r.lockRule ?? "",
  unlockIntimacyLv: num(r.unlockIntimacyLv),
  sortOrder: num(r.sortOrder),
  publishAtMs: num(r.publishAtMs),
  enabled: !!r.enabled,
});

export const toServerIdolPhotoBody = (p: IdolPhoto): IdolPhotoBody => ({
  uid: p.localKey,
  puzzleId: p.puzzleId,
  slot: p.slot,
  thumbImg: p.thumbImg,
  img: p.img,
  unlockRule: p.unlockRule,
  unlockIntimacyLv: p.unlockIntimacyLv,
  enabled: p.enabled,
});

export const toServerIdolVipPhotoBody = (p: IdolVipPhoto): IdolVipPhotoBody => ({
  uid: p.localKey,
  idolId: p.idolId,
  thumbImg: p.thumbImg,
  img: p.img,
  lockRule: p.lockRule,
  unlockIntimacyLv: p.unlockIntimacyLv,
  sortOrder: p.sortOrder,
  publishAtMs: p.publishAtMs,
  enabled: p.enabled,
});

/**
 * 时长与 videoUrl **原样回传**（读回来是什么就发什么）：PUT 是整行 UPDATE，
 * 不带的话服务端按 null 写，等于运营改个排序就把已传好的视频洗掉
 */
export const toServerIdolVipVideoBody = (p: IdolVipVideo): IdolVipVideoBody => ({
  uid: p.localKey,
  idolId: p.idolId,
  coverImg: p.coverImg,
  durationSec: p.durationSec,
  videoUrl: p.videoUrl,
  lockRule: p.lockRule,
  unlockIntimacyLv: p.unlockIntimacyLv,
  sortOrder: p.sortOrder,
  publishAtMs: p.publishAtMs,
  enabled: p.enabled,
});

export const isNewIdolPhoto = (p: IdolPhoto) => p.photoId == null;
export const isNewIdolVipPhoto = (p: IdolVipPhoto) => p.vipPhotoId == null;
export const isNewIdolVipVideo = (p: IdolVipVideo) => p.vipVideoId == null;

const uuid = () => globalThis.crypto.randomUUID();

/**
 * 新建一行。默认启用、规则取「最不容易配错」的那个（相册 = 拼完解锁 / VIP = 会员可见），等级 0；
 * 图留空由运营自己传——替他猜等于塞假配置。
 */
export const newIdolPhoto = (puzzleId: string, slot: number): IdolPhoto => ({
  photoId: null, localKey: uuid(), puzzleId, slot, thumbImg: "", img: "",
  unlockRule: "stage_assembled", unlockIntimacyLv: 0, enabled: true,
});
export const newIdolVipPhoto = (idolId: string, sortOrder = 0): IdolVipPhoto => ({
  vipPhotoId: null, localKey: uuid(), idolId, thumbImg: "", img: "",
  lockRule: "vip", unlockIntimacyLv: 0, sortOrder, publishAtMs: 0, enabled: true,
});
export const newIdolVipVideo = (idolId: string, sortOrder = 0): IdolVipVideo => ({
  vipVideoId: null, localKey: uuid(), idolId, coverImg: "", durationSec: null, videoUrl: null,
  lockRule: "vip", unlockIntimacyLv: 0, sortOrder, publishAtMs: 0, enabled: true,
});

// ─────────────────────────────────────────────────────────────────────────────
// 规则 ↔ 等级
// ─────────────────────────────────────────────────────────────────────────────

/**
 * 规则与等级配不配套；null = 合法。口径抄服务端 `albumRuleFault`：
 * intimacy_lv 时等级 ≥ 1，其它规则等级必须 = 0。
 */
export function ruleLevelFault(rule: string, lv: number, allowed: readonly string[]): MsgKey | null {
  if (!allowed.includes(rule)) return "album.iss.badRule";
  if (!Number.isInteger(lv) || lv < 0 || lv > ALBUM_INT_MAX) return "album.iss.badLevel";
  if (rule === RULE_INTIMACY && lv < 1) return "album.iss.needLevel";
  if (rule !== RULE_INTIMACY && lv !== 0) return "album.iss.levelMustBeZero";
  return null;
}

/**
 * 换规则时把等级一起摆正：换成 intimacy_lv 至少给 1（0 存不下去），换成别的强制 0。
 * 只在**换规则**这一下摆正，等级输入框本身对非 intimacy_lv 是禁用的
 */
export function withRule<T extends { unlockIntimacyLv: number }>(row: T, ruleKey: keyof T, rule: string): T {
  const lv = rule === RULE_INTIMACY ? Math.max(1, row.unlockIntimacyLv) : 0;
  return { ...row, [ruleKey]: rule, unlockIntimacyLv: lv };
}

/** 等级输入框能不能填：只有 intimacy_lv 看等级 */
export const levelEditable = (rule: string) => rule === RULE_INTIMACY;

// ─────────────────────────────────────────────────────────────────────────────
// 问题清单与保存闸（同 puzzleIssues / puzzleBlocker：一个来源，弹窗与列表共用）
// ─────────────────────────────────────────────────────────────────────────────

/**
 * 关卡相册一行的问题（i18n key），顺序 = 弹窗里的字段顺序。
 * `puzzleIds` = 本版本里已落库的拼图（拼图删了，挂在它下面的照片发布时会被 IDOL_PHOTO_PUZZLE_EXISTS 拦）。
 * **不含 slot 重复**：那是非阻断提示（草稿允许暂时重复，见 duplicateSlotKeys）
 */
export function idolPhotoIssues(p: IdolPhoto, puzzleIds: ReadonlySet<string>): MsgKey[] {
  const out: MsgKey[] = [];
  if (!p.puzzleId || !puzzleIds.has(p.puzzleId)) out.push("album.iss.noPuzzle");
  if (!Number.isInteger(p.slot) || p.slot < PHOTO_SLOT_MIN || p.slot > ALBUM_INT_MAX) out.push("album.iss.badSlot");
  if (!p.thumbImg || !p.img) out.push("album.iss.noImg");
  const rf = ruleLevelFault(p.unlockRule, p.unlockIntimacyLv, PHOTO_UNLOCK_RULES);
  if (rf) out.push(rf);
  return out;
}

export function vipPhotoIssues(p: IdolVipPhoto, idolIds: ReadonlySet<string> | null): MsgKey[] {
  const out: MsgKey[] = [];
  // 名册没拉到（null）时不判 idol 存在：拉不到不等于不存在，别把整页标红
  if (!p.idolId || (idolIds && !idolIds.has(p.idolId))) out.push("album.iss.noIdol");
  if (!p.thumbImg || !p.img) out.push("album.iss.noImg");
  const rf = ruleLevelFault(p.lockRule, p.unlockIntimacyLv, VIP_LOCK_RULES);
  if (rf) out.push(rf);
  if (!Number.isInteger(p.publishAtMs) || p.publishAtMs < 0) out.push("album.iss.badPublishAt");
  return out;
}

/** 时长 / videoUrl 不在这里判：运营改不了它们，判出来也无从下手（发布闸 IDOL_VIP_VIDEO_DURATION 兜底） */
export function vipVideoIssues(p: IdolVipVideo, idolIds: ReadonlySet<string> | null): MsgKey[] {
  const out: MsgKey[] = [];
  if (!p.idolId || (idolIds && !idolIds.has(p.idolId))) out.push("album.iss.noIdol");
  if (!p.coverImg) out.push("album.iss.noCover");
  const rf = ruleLevelFault(p.lockRule, p.unlockIntimacyLv, VIP_LOCK_RULES);
  if (rf) out.push(rf);
  if (!Number.isInteger(p.publishAtMs) || p.publishAtMs < 0) out.push("album.iss.badPublishAt");
  return out;
}

/** 存不下去的第一条原因；null = 能存。**保存闸的唯一口径**（同 puzzleBlocker：从问题清单取第一条，不另写） */
export const idolPhotoBlocker = (p: IdolPhoto, puzzleIds: ReadonlySet<string>): MsgKey | null =>
  idolPhotoIssues(p, puzzleIds)[0] ?? null;
export const vipPhotoBlocker = (p: IdolVipPhoto, idolIds: ReadonlySet<string> | null): MsgKey | null =>
  vipPhotoIssues(p, idolIds)[0] ?? null;
export const vipVideoBlocker = (p: IdolVipVideo, idolIds: ReadonlySet<string> | null): MsgKey | null =>
  vipVideoIssues(p, idolIds)[0] ?? null;

/** 缩略图与原图是同一张：缩略图会发给没解锁的玩家，等于原图白送。非阻断（发布闸也只是 warn） */
export const thumbSameAsImg = (p: { thumbImg: string; img: string }) => !!p.thumbImg && p.thumbImg === p.img;

/**
 * 同一关里启用行 slot 撞了的那些行（localKey）。与发布闸 IDOL_PHOTO_SLOT_UNIQUE 同一口径：只看启用行。
 * **只提示、不拦**：对调两张的 slot 时第一笔保存落库那一刻两张必然一样，拦了就永远换不过去
 */
export function duplicateSlotKeys(photos: IdolPhoto[]): Set<string> {
  const groups = new Map<string, IdolPhoto[]>();
  for (const p of photos) {
    if (!p.enabled) continue;
    const k = `${p.puzzleId}\u0000${p.slot}`;
    groups.set(k, [...(groups.get(k) ?? []), p]);
  }
  const out = new Set<string>();
  for (const g of groups.values()) if (g.length > 1) for (const p of g) out.add(p.localKey);
  return out;
}

/** 这一关下一张配第几张：已有最大 slot + 1，至少 2 */
export const nextSlotOf = (photos: IdolPhoto[], puzzleId: string): number =>
  Math.max(PHOTO_SLOT_MIN - 1, ...photos.filter((p) => p.puzzleId === puzzleId).map((p) => p.slot)) + 1;

/** 这位 idol 下一条的排序值：已有最大 + 1（新建默认排最后） */
export const nextSortOf = (rows: Array<{ idolId: string; sortOrder: number }>, idolId: string): number => {
  const xs = rows.filter((r) => r.idolId === idolId).map((r) => r.sortOrder);
  return xs.length ? Math.max(...xs) + 1 : 0;
};

/** 时长展示：null → 「—」；有值 → m:ss */
export const formatDuration = (sec: number | null): string => {
  if (sec == null) return "—";
  const s = Math.max(0, Math.round(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};
